/** One bar of the "By hour" strip. `hourStart` is a UTC hour, ISO-8601. */
export interface HourCount {
    readonly hourStart: string;
    readonly count: number;
}

const HOUR_MS = 60 * 60 * 1000;

/**
 * One entry per UTC hour of the window, oldest first, missing hours at zero.
 *
 * Built on epoch hours, never on local wall-clock time, so a day with a
 * daylight-saving change still has 24 bars. An empty answer is a row of zeros,
 * never an absent strip (a range with no refusals is an answer, not a gap).
 */
export function fillHours(
    byHour: readonly HourCount[],
    from: string,
    to: string,
): readonly HourCount[] {
    const start = Math.floor(Date.parse(from) / HOUR_MS) * HOUR_MS;
    const end = Date.parse(to);
    if (Number.isNaN(start) || Number.isNaN(end)) return [];

    const counts = new Map<number, number>();
    for (const { hourStart, count } of byHour) {
        const hour = Date.parse(hourStart);
        if (Number.isNaN(hour)) continue;
        counts.set(hour, (counts.get(hour) ?? 0) + count);
    }

    const series: HourCount[] = [];
    for (let hour = start; hour < end; hour += HOUR_MS) {
        series.push({ hourStart: new Date(hour).toISOString(), count: counts.get(hour) ?? 0 });
    }
    return series;
}

/** The busiest hour, the earliest on a tie; `null` when nothing was refused. */
export function peakHour(series: readonly HourCount[]): HourCount | null {
    let peak: HourCount | null = null;
    for (const entry of series) {
        if (entry.count > (peak?.count ?? 0)) peak = entry;
    }
    return peak;
}
